import { useTextTranslation } from "../../utils/useTextTranslation";
import React, { useEffect, useMemo, useState } from "react";
import useNearViewport from "../../utils/useNearViewport";
import moment from "moment";
import { getProgramsById } from "../../api/ltv/ltv";
import { getBroadcastCopy } from "../../statics/broadcastCopy";
import { programPlacement } from "./guideTimeline";
import CinemaIcon from "../cinema/CinemaIcon";

export default function TvGuideRow({
  channel,
  date,
  dayStart,
  dayEnd,
  now,
  viewportRef,
  selectedChannel,
  onWatch,
  onClickProgram
}) {
  const translateText = useTextTranslation();
  const copy = getBroadcastCopy();
  const [rowRef, ready] = useNearViewport({ rootRef: viewportRef, margin: "240px 0px" });
  const [programs, setPrograms] = useState([]);
  const [status, setStatus] = useState("loading");
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    if (!ready) return;
    let active = true;
    setStatus("loading");
    setPrograms([]);
    getProgramsById(channel.id, date)
      .then((response) => {
        if (!active) return;
        setPrograms(Array.isArray(response?.data) ? response.data : []);
        setStatus("ready");
      })
      .catch(() => {
        if (active) setStatus("error");
      });
    return () => {
      active = false;
    };
  }, [channel.id, date, retry, ready]);
  const visible = useMemo(() => programs
    .map((program) => ({
      program,
      placement: programPlacement(program, dayStart, dayEnd)
    }))
    .filter((item) => item.placement)
    .sort((a, b) => a.placement.start - b.placement.start), [programs, dayStart, dayEnd]);
  return (
    <div className="epg-row" ref={rowRef}>
      <button
        className={`epg-channel ${
          selectedChannel?.id === channel.id ? "is-selected" : ""
        }`}
        type="button"
        onClick={() => onWatch(channel)}
        title={`${copy.watch} · ${channel.name}`}
      >
        <span className="epg-channel-logo">
          <img
            src={channel.logo}
            alt=""
            loading="lazy"
            onError={(event) => {
              event.currentTarget.style.visibility = "hidden";
            }}
          />
        </span>
        <span className="epg-channel-name">{channel.name}</span>
        <CinemaIcon name="play" width="16" height="16" />
      </button>
      <div className="epg-row-track">
        {status === "loading" ? (
          <div className="epg-row-message epg-loading" role="status">
            {copy.loading}
          </div>
        ) : status === "error" ? (
          <div className="epg-row-message">
            {copy.error}{" "}
            <button onClick={() => setRetry((value) => value + 1)}>
              {copy.retry}
            </button>
          </div>
        ) : !visible.length ? (
          <div className="epg-row-message">{copy.noPrograms}</div>
        ) : (
          visible.map(({ program, placement }) => {
            const current = placement.start <= now && now < placement.end;
            const past = placement.end <= now;
            return (
              <button
                type="button"
                key={`${program.epgProgramId || program.title}-${
                  placement.start
                }`}
                className={`epg-program ${current ? "is-current" : ""} ${
                  past ? "is-past" : ""
                } ${placement.width < 100 ? "is-short" : ""}`}
                style={{
                  insetInlineStart: placement.left + 3,
                  width: Math.max(1, placement.width - 6)
                }}
                title={`${translateText(program.title)} · ${moment(placement.start).format(
                  "HH:mm"
                )}–${moment(placement.end).format("HH:mm")}`}
                onClick={() =>
                  onClickProgram(
                    {
                      ...program,
                      channelUId: channel.id,
                      channelId: channel.id,
                      channelcode: channel.channelcode
                    },
                    current,
                    channel
                  )
                }
              >
                <span className="epg-program-copy">
                  <span className="epg-program-time">
                    {moment(placement.start).format("HH:mm")} —{" "}
                    {moment(placement.end).format("HH:mm")}
                    {current && (
                      <span className="epg-on-air">
                        <i />
                        {copy.liveBadge}
                      </span>
                    )}
                  </span>
                  <strong>{translateText(program.title)}</strong>
                  {program.category && (
                    <span className="epg-program-category">
                      {program.category}
                    </span>
                  )}
                </span>
                {current && (
                  <span className="epg-progress">
                    <i
                      style={{
                        width: `${Math.min(
                          100,
                          Math.max(
                            0,
                            ((now - placement.start) /
                              (placement.end - placement.start)) *
                              100
                          )
                        )}%`
                      }}
                    />
                  </span>
                )}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
